import {lory} from 'lory.js';
import du from '../utils/dom-utilities';
import scrollTo from '../utils/ScrollTo';
import isScrolledIntoView from '../utils/isScrolledIntoView';

/**
 * setScrollTo - Animate scroll to node
 *
 * @param {HTMLElement} [destinationNode=null] - desintation scroll to node
 */
const setScrollTo = (destinationNode = null) => {
    if (!destinationNode) {
        return;
    }

    let bodyRect = document.body.getBoundingClientRect();
    let destinationRect = destinationNode.getBoundingClientRect();
    let offset = destinationRect.top - (bodyRect.top - 10);

    scrollTo(document.scrollingElement || document.documentElement, offset, 400);
};

/**
 * getMenuLimit - Get anchors Menu Width
 *
 * @param  {HTMLElement} [anchorsItems=null] - DOM nodes of anchors
 * @return {Number} - Anchors menu width
 */
const getMenuLimit = (menu = null) => {
    if (!menu) {
        throw new Error('menu param cannot be null');
    }

    menu = document.querySelector(menu);
    let container = du.getClosest(menu, '.container');

    if (!menu) {
        return;
    }

    if (!container) {
        return menu.offsetWidth;
    }

    let menuItemsWidth = menu.offsetWidth;
    menuItemsWidth += parseFloat(window.getComputedStyle(container).getPropertyValue('padding-left'));
    menuItemsWidth += parseFloat(window.getComputedStyle(container).getPropertyValue('padding-right'));

    return menuItemsWidth;
};

/* eslint-disable max-statements */
class Anchors {
    constructor() {
        this._anchors = [];
        this._isNavigating = false;
    }

    /**
     * init - Get anchors template based on anchors DOM Tree
     *
     * @param  {String} [elements='.anchor__content'] - DOM node selector
     * @return {HTMLElement}
     */
    init(elements = null, target = null) {
        if (!elements || !target) {
            throw new Error('elements param or target param cannot be null.');
        }

        let domAnchors = du.queryAll(elements);
        target = document.querySelector(target);

        if (!domAnchors || !target) {
            return; // Silent error
        }

        domAnchors.forEach((anchor) => {
            this._anchors.push(this._buildAnchor(anchor));
        });

        target.innerHTML += this._buildAnchors(this._anchors);
        let anchorsWrapper = document.querySelector('.js-anchors-wrapper');
        this._setupAnchorSlider(anchorsWrapper, '.anchors__items', 'js-anchors--active');

        document.body.classList.add('has-anchors');


        this._toggleActiveClass();
        this._setAnchorClickEvent(target);

        let anchors = du.queryAll('.anchor__content');
        if (anchors.length > 0) {
            let href = anchors[0].getAttribute('id');
            if (href) {
                document
                    .querySelector(`[href="#${href}"]`)
                    .classList.add('anchors__link--active');
            }
        }
    }

    /**
     * Add active class on anchor__link if DOM element has been scrolled
     */
    _toggleActiveClass() {
        var previous = window.scrollY;
        du.onEvent(window, 'scroll', () => {
            let anchors = du.queryAll('.anchor__content');
            const scrollDirection = window.scrollY > previous ? 'down' : 'up';
            previous = window.scrollY;

            anchors.forEach((anchor, index) => {

                if (isScrolledIntoView(anchor)) {
                    if (scrollDirection === 'up' && index > 0) {
                        anchor = anchors[index - 1];
                    }

                    let href = anchor.getAttribute('id');
                    if (href) {
                        let link = document.querySelector(`[href="#${href}"]`);

                        if (!link.classList.contains('anchor__link--active')) {
                            du.queryAll('.anchors__link')
                                .forEach((link) => {
                                    link.classList.remove('anchors__link--active');
                                });
                            link.classList.add('anchors__link--active');
                        } else {
                            link.classList.remove('anchors__link--active');
                        }
                    }
                }
            });
        });

    }

    /**
     * buildAnchor - build an object with anchor details
     *
     * @param  {HTMLElement} [anchor=null] - anchor DOM element
     * @return {Object} - Object with anchor details (value, isExternal, template)
     */
    _buildAnchor(anchor = null) {
        if (!anchor) {
            throw new Error('anchor object cannot be blank.');
        }

        let name = anchor.getAttribute('data-name');
        let value = anchor.getAttribute('id') ? anchor.getAttribute('id') : anchor.getAttribute('data-url');
        let isExternal = anchor.getAttribute('data-is-external');
        let template = this._buildAnchorTemplate(name, value, isExternal);

        return {name, value, isExternal, template};
    }

    /**
     * buildAnchors - build anchors markup
     *
     * @param  {Array}  [anchors=[]] - Array of anchor object
     * @return {HTMLElement} return a template string  <ul><li><a>Anchor</a></li></ul>
     */
    _buildAnchors() {
        if (this._anchors.length <= 0) {
            throw new Error('Anchors array cannot be empty.');
        }

        let template = this._separateAnchors(this._anchors);

        return `<ul class="anchors__items">
                    <li class="js-anchors-next">${QUEBECOR_TRANSLATE.swipe}</li>
                    ${template}
                </ul>`;
    }

    /**
     * separateAnchors - separate anchors by isExternal and build template
     *
     * @param  {Array}  [anchors=[]] - Array of anchor object
     * @return {HTMLElement} return a template string  <li><a>Anchor</a></li>
     */
    _separateAnchors(anchors = []) {
        let anchorsInner = anchors.filter((anchor) => anchor.isExternal === 'false')
            .map((anchor) => anchor.template).join('');

        let anchorsExternal = anchors.filter((anchor) => anchor.isExternal === 'true')
            .map((anchor) => anchor.template).join('');


        if (anchorsExternal === '') {
            return `${anchorsInner}`;
        }

        return `${anchorsInner}
            <li class="anchors__item anchors__item--external">
                <ul class="anchors__items">${anchorsExternal}</ul>
            </li>`;
    }

    /**
     * buildAnchorTemplate - build a template for a single anchor
     *
     * @param  {String}  [value=null] - value of Anchor
     * @param  {String} [isExternal=false] - Is an external link or not
     * @return {HTMLElement} return a template string - <li><a>Anchor</a></li>
     */
    _buildAnchorTemplate(name = null, value = null, isExternal = 'false') {
        if (!value || !name) {
            throw new Error('name param or value param cannot be blank.');
        }

        if (isExternal === 'true') {
            return `<li class="anchors__item anchor__item--external">
                <a href="${value}" rel="external" class="anchors__link" target="_blank">
                    ${name}
                    <svg class="icon"><use xlink:href="#external"/></svg>
                </a>
            </li>`;
        }

        return `<li class="anchors__item">
            <a href="#${value}" class="anchors__link">${name}</a>
        </li>`;
    }

    /**
     * setupAnchorSlider - Define slider for anchors menu
     *
     * @param  {HTMLElement} [anchorsWrapper=null] - Anchors wrapper DOM Node
     * @param  {HTMLElement} [anchors=null] - DOM nodes of anchors
     * @param  {HTMLElement} [container=null] - Container which wrap anchors
     * @param  {String} [activeClass=null] - Active slider class
     * @return {Set} set Lory slider instance
     */
    _setupAnchorSlider(anchorsWrapper = null, anchors = null, activeClass = null) {
        if (!anchorsWrapper || !anchors || !activeClass) {
            return;
        }

        let loryOptions = {
            enableMouseEvents: true,
            classNameFrame: 'js-anchors-frame',
            classNameSlideContainer: 'anchors__items',
            slidesToScroll: 1
        };

        const anchorsParent = du.getClosest(anchorsWrapper, '.anchors');
        const instance = lory(anchorsWrapper, loryOptions);

        if (window.innerWidth <= getMenuLimit(anchors)) {
            anchorsParent.classList.add(activeClass);
            instance.setup();
        } else {
            instance.destroy();
        }

        du.onEvent(window, 'optimized-resize', () => {
            if (window.innerWidth <= getMenuLimit(anchors)) {
                anchorsParent.classList.add(activeClass);
                instance.setup();
            } else {
                anchorsParent.classList.remove(activeClass);
                instance.destroy();
            }
        });

        anchorsWrapper.addEventListener('on.lory.touchstart', (event) => {
            this._isNavigating = true;
            event.preventDefault();
            event.stopPropagation();
        });

        anchorsWrapper.addEventListener('on.lory.touchmove', (event) => {
            this._isNavigating = true;
            event.preventDefault();
            event.stopPropagation();
        });

        anchorsWrapper.addEventListener('on.lory.touchend', () => {
            this._isNavigating = !this._isNavigating;
        });

        anchorsWrapper.addEventListener('before.lory.slide', (event) => {
            this._isNavigating = true;
            event.preventDefault();
            event.stopPropagation();
        });

        anchorsWrapper.addEventListener('after.lory.slide', () => {
            this._isNavigating = !this._isNavigating;
        });
    }

    /**
     * setAnchorClickEvent - Set click event on anchor link
     *
     * @param {HTMLElement} [target=null] - Anchor link DOM element
     */
    _setAnchorClickEvent(target = null) {
        if (!target) {
            throw new Error('target param cannot be empty');
        }

        du.queryAll('.anchors__link:not([rel="external"])')
            .forEach((item) => {

                du.onEvents(item, 'click onTouchStart', (event) => {
                    if (this._isNavigating) {
                        event.preventDefault();
                        event.stopPropagation();
                        return false;
                    }

                    let linkHref = event.target.getAttribute('href');

                    if (!linkHref) {
                        return;
                    }

                    let destinationNode = document.querySelector(linkHref);

                    if (!destinationNode) {
                        return;
                    }

                    event.target.classList.add('anchor__link-is--active');

                    history.pushState(null, null, linkHref);

                    setScrollTo(destinationNode);
                });
            });
    }
}
/* eslint-enable max-statements */
export {Anchors, getMenuLimit, setScrollTo};



// WEBPACK FOOTER //
// ./src/main/js/components/anchor.js